/**
 * Event Builder Utilities
 * Helper functions to create standardized AnalyticsEvent objects
 */

import { AnalyticsEvent, EventContext } from '../interfaces/models/analytics-event.interface';
import { VariantType } from '../interfaces/models/ab-test-config.interface';

interface BaseEventParams {
  eventId: string;
  userId: string;
  sessionId: string;
  journeyId?: string;
  testId?: string;
  variantId?: VariantType;
  timestamp?: Date;
  context: EventContext;
}

interface JourneyEventParams extends BaseEventParams {
  eventName: string;
  page: string;
  normalizedPage?: string;  // Normalized URL pattern
  action: string;
  elementId?: string;
  durationMs?: number;
  pageLoadTimeMs?: number;
  additionalProperties?: Record<string, unknown>;
}

interface FunnelStepEventParams extends BaseEventParams {
  funnelId: string;
  funnelStep: string;
  page: string;
  normalizedPage?: string;  // Normalized URL pattern
  durationSincePreviousStep?: number;
  timeToConversionMs?: number;
  additionalProperties?: Record<string, unknown>;
}

interface ConversionEventParams extends BaseEventParams {
  testId: string; // Required for conversions
  variantId: VariantType; // Required for conversions
  metricId: string;
  value?: number;
  timeToConversionMs?: number | null;
  scrollDepth?: {
    scrolledPixels: number;
    scrolledPercent: number;
  } | null;
  additionalProperties?: Record<string, unknown>;
}

interface ImpressionEventParams extends BaseEventParams {
  testId: string; // Required for impressions
  variantId: VariantType; // Required for impressions
  eventName?: string; // Optional custom event name (defaults to 'variant_shown')
  scrollDepth?: {
    scrolledPixels: number;
    scrolledPercent: number;
  } | null;
  additionalProperties?: Record<string, unknown>;
}

interface InteractionEventParams extends BaseEventParams {
  testId: string; // Required for interactions
  variantId: VariantType; // Required for interactions
  eventName: string;
  page?: string;
  normalizedPage?: string;  // Normalized URL pattern
  action?: string;
  scrollDepth?: {
    scrolledPixels: number;
    scrolledPercent: number;
  } | null;
  additionalProperties?: Record<string, unknown>;
}

/**
 * Create a journey event (page view, navigation, interaction)
 */
export function createJourneyEvent(params: JourneyEventParams): AnalyticsEvent {
  return {
    eventId: params.eventId,
    userId: params.userId,
    sessionId: params.sessionId,
    journeyId: params.journeyId,
    testId: params.testId || null,
    variantId: params.variantId || null,
    eventType: 'page_view', // Default journey event type
    eventName: params.eventName,
    eventCategory: 'user-journey',
    timestamp: params.timestamp || new Date(),

    // Top-level timing metrics
    durationMs: params.durationMs || null,
    timeToConversionMs: null,
    pageLoadTimeMs: params.pageLoadTimeMs || null,

    // Top-level scroll metrics
    scrolledPixels: null,
    scrolledPercent: null,

    // Funnel metrics
    funnelId: null,
    funnelStep: null,

    // Other top-level metrics
    page: params.page,
    normalizedPage: params.normalizedPage || null,
    action: params.action,
    value: null,

    context: params.context,

    properties: {
      elementId: params.elementId,
      ...params.additionalProperties,
    },
  };
}

/**
 * Create a funnel step event
 */
export function createFunnelStepEvent(params: FunnelStepEventParams): AnalyticsEvent {
  return {
    eventId: params.eventId,
    userId: params.userId,
    sessionId: params.sessionId,
    journeyId: params.journeyId,
    testId: params.testId || null,
    variantId: params.variantId || null,
    eventType: 'funnel_step',
    eventName: 'funnel_step',
    eventCategory: 'user-journey',
    timestamp: params.timestamp || new Date(),

    // Top-level timing metrics
    durationMs: params.durationSincePreviousStep || null,
    timeToConversionMs: params.timeToConversionMs || null,
    pageLoadTimeMs: null,

    // Top-level scroll metrics
    scrolledPixels: null,
    scrolledPercent: null,

    // Other top-level metrics
    funnelId: params.funnelId,
    funnelStep: params.funnelStep,
    page: params.page,
    normalizedPage: params.normalizedPage || null,
    action: 'step_completed',
    value: null,

    context: params.context,

    properties: {
      durationSincePreviousStep: params.durationSincePreviousStep,
      ...params.additionalProperties,
    },
  };
}

/**
 * Create a conversion event
 */
export function createConversionEvent(params: ConversionEventParams): AnalyticsEvent {
  return {
    eventId: params.eventId,
    userId: params.userId,
    sessionId: params.sessionId,
    journeyId: params.journeyId,
    testId: params.testId,
    variantId: params.variantId,
    eventType: 'conversion',
    eventName: params.metricId,
    eventCategory: 'ab-test',
    timestamp: params.timestamp || new Date(),

    // Top-level timing metrics
    durationMs: null,
    timeToConversionMs: params.timeToConversionMs || null,

    pageLoadTimeMs: null,

    // Top-level scroll metrics
    scrolledPixels: params.scrollDepth?.scrolledPixels || null,
    scrolledPercent: params.scrollDepth?.scrolledPercent || null,

    // Funnel metrics
    funnelId: null,
    funnelStep: null,

    // Journey metrics
    page: null,
    normalizedPage: null,
    action: null,

    // Other top-level metrics
    value: params.value || null,

    context: params.context,

    properties: {
      metricId: params.metricId,
      ...params.additionalProperties,
    },
  };
}

/**
 * Create an impression event
 */
export function createImpressionEvent(params: ImpressionEventParams): AnalyticsEvent {
  return {
    eventId: params.eventId,
    userId: params.userId,
    sessionId: params.sessionId,
    journeyId: params.journeyId,
    testId: params.testId,
    variantId: params.variantId,
    eventType: 'impression',
    eventName: params.eventName || 'variant_shown',
    eventCategory: 'ab-test',
    timestamp: params.timestamp || new Date(),

    // Top-level timing metrics
    durationMs: null,
    timeToConversionMs: null,

    pageLoadTimeMs: null,

    // Top-level scroll metrics
    scrolledPixels: params.scrollDepth?.scrolledPixels || null,
    scrolledPercent: params.scrollDepth?.scrolledPercent || null,

    // Funnel metrics
    funnelId: null,
    funnelStep: null,

    // Journey metrics
    page: null,
    normalizedPage: null,
    action: null,

    // Other top-level metrics
    value: null,

    context: params.context,

    properties: params.additionalProperties || {},
  };
}

/**
 * Create a generic interaction event
 */
export function createInteractionEvent(params: InteractionEventParams): AnalyticsEvent {
  return {
    eventId: params.eventId,
    userId: params.userId,
    sessionId: params.sessionId,
    journeyId: params.journeyId,
    testId: params.testId,
    variantId: params.variantId,
    eventType: 'interaction',
    eventName: params.eventName,
    eventCategory: 'ab-test',
    timestamp: params.timestamp || new Date(),

    // Top-level timing metrics
    durationMs: null,
    timeToConversionMs: null,

    pageLoadTimeMs: null,

    // Top-level scroll metrics
    scrolledPixels: params.scrollDepth?.scrolledPixels || null,
    scrolledPercent: params.scrollDepth?.scrolledPercent || null,

    // Funnel metrics
    funnelId: null,
    funnelStep: null,

    // Other top-level metrics
    page: params.page || null,
    normalizedPage: params.normalizedPage || null,
    action: params.action || null,
    value: null,

    context: params.context,

    properties: params.additionalProperties || {},
  };
}

/**
 * Create event context from browser environment
 */
export function createEventContext(deviceType: 'mobile' | 'tablet' | 'desktop'): EventContext {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      userAgent: 'unknown',
      url: 'unknown',
      viewport: { width: 0, height: 0 },
      deviceType: 'desktop',
    };
  }

  return {
    userAgent: navigator.userAgent,
    url: window.location.href,
    referrer: document.referrer || undefined,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    deviceType,
  };
}
